/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import { AbsoluteTime, TalerMerchantApi, URL } from "@gnu-taler/taler-util";
import { Time, useTranslationContext } from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { Tooltip } from "../../../../components/Tooltip.js";

const TALER_SCREEN_ID = 41;

export interface Props {
  exchanges: TalerMerchantApi.ExchangeStatusDetail[];
  onSelect: (e: TalerMerchantApi.ExchangeStatusDetail) => void;
}

export function ListPage({ exchanges, onSelect }: Props): VNode {
  const { i18n } = useTranslationContext();

  return (
    <section class="section is-main-section">
      <div class="card has-table">
        <header class="card-header">
          <p class="card-header-title">
            <i class="icon mdi mdi-clock" />
            <i18n.Translate>Supported exchange status</i18n.Translate>
          </p>

          <div class="card-header-icon" aria-label="more options" />
        </header>
        <div class="card-content">
          <div class=" has-pagination">
            <div class="table-wrapper has-mobile-cards">
              {exchanges.length > 0 ? (
                <PendingTable exchanges={exchanges} onSelect={onSelect} />
              ) : (
                <EmptyTable />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
interface PendingTableProps {
  exchanges: TalerMerchantApi.ExchangeStatusDetail[];
  onSelect: (e: TalerMerchantApi.ExchangeStatusDetail) => void;
}

function PendingTable({ exchanges, onSelect }: PendingTableProps): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="">
      <table class="table is-striped is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>
              <i18n.Translate>Base URL</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Status</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Next update</i18n.Translate>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {exchanges.map((e, i) => {
            const isWorking =
              e.keys_http_status >= 200 && e.keys_http_status < 300;
            const legend = isWorking
              ? i18n.str`This exchange is ready to be used.`
              : i18n.str`This exchange can't be used due to an error. Contact the service provider.`;
            const url = new URL(`/config`, e.exchange_url);
            return (
              <tr key={i}>
                <td>
                  <Tooltip text={legend}>
                    <a
                      href={url.href}
                      target="_blank"
                      referrerpolicy="no-referrer"
                    >
                      {e.exchange_url}
                    </a>
                  </Tooltip>
                </td>
                <td>
                  <Tooltip text={legend}>
                    {isWorking ? (
                      <div style={{ color: "green" }}>
                        <i class="icon mdi mdi-check" />
                        {e.keys_http_status}
                      </div>
                    ) : (
                      <div style={{ color: "red" }}>
                        <i class="icon mdi mdi-close" />
                        {e.keys_http_status}
                      </div>
                    )}
                  </Tooltip>
                </td>
                <td>
                  <Tooltip text={legend}>
                    <Time
                      format="dd/MM/yyyy HH:mm:ss"
                      timestamp={AbsoluteTime.fromProtocolTimestamp(
                        e.next_download,
                      )}
                    />
                  </Tooltip>
                </td>
                <td class="is-actions-cell right-sticky">
                  <div key={i} class="buttons is-right">
                    <Tooltip text={i18n.str`Details of the status`}>
                      <button
                        class="button is-small is-info"
                        type="button"
                        onClick={() => onSelect(e)}
                      >
                        <i18n.Translate>Details</i18n.Translate>
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EmptyTable(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="content has-text-grey has-text-centered">
      <p>
        <span class="icon is-large">
          <i class="icon mdi mdi-emoticon-happy mdi-48px" />
        </span>
      </p>
      <p>
        <i18n.Translate>No pending kyc verification!</i18n.Translate>
      </p>
    </div>
  );
}
